import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  { value: "partnership", label: "Founding GCC / long-term partnership" },
  { value: "engagement", label: "AI pod or project engagement" },
  { value: "research", label: "Research / prototyping sprint" },
  { value: "media", label: "Press or analyst inquiry" },
  { value: "other", label: "Something else" },
];

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    role: "",
    inquiry_type: "partnership",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Thanks — we’ll be in touch within 2 business days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-bone">
      {/* HERO */}
      <section className="pt-20 md:pt-28 pb-16 border-b border-border">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline text-vermilion mb-8">Contact</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Tell us what you’re
              <br />
              building.
              <br />
              <span className="italic text-ink/60">We’ll tell you honestly
              if we can help.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l border-border">
            <p className="text-lg text-ink/70 leading-relaxed">
              A short note to our founders goes a long way. We read every message
              ourselves, and respond within two business days.
            </p>
            <div className="mt-8 space-y-4 text-sm">
              <div>
                <div className="overline text-vermilion mb-1">Email</div>
                <a
                  href="mailto:contact@thinkright-tech.com"
                  className="link-underline"
                  data-testid="contact-email-link"
                >
                  contact@thinkright-tech.com
                </a>
              </div>
              <div>
                <div className="overline text-vermilion mb-1">Offices</div>
                <div className="space-y-1 text-ink">
                  <div>Chandigarh · India</div>
                  <div>New York · United States</div>
                  <div>
                    Bengaluru · India
                    <div className="text-ink/60 text-xs mt-1 leading-relaxed">
                      BHIVE Workspace, No. 112, AKR Tech Park<br />
                      ‘A’ &amp; ‘B’ Block, 7th Mile Hosur Rd, Kudlu Gate,<br />
                      Krishna Reddy Industrial Area,<br />
                      Bengaluru, Karnataka 560068
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="overline text-vermilion mb-1">Serving</div>
                <div className="text-ink">US · EU · IN</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="py-20 md:py-28">
        <div className="tr-container grid grid-cols-12 gap-10">
          <Reveal className="col-span-12 md:col-span-4">
            <div className="overline text-vermilion mb-4">Send a note</div>
            <p className="text-ink/70 leading-relaxed">
              Share the context: what you’re building, where you are in the
              journey, and what kind of partnership you’re imagining.
            </p>
            <ul className="mt-10 space-y-4 text-sm text-ink/70">
              {[
                "No sales follow-ups. Ever.",
                "NDA-friendly — we work under founder-level confidentiality by default.",
                "Typical response time: under 48 hours.",
              ].map((l) => (
                <li key={l} className="flex gap-3 border-t border-border pt-4">
                  <span className="font-mono text-xs text-vermilion pt-1">→</span>
                  {l}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={1} className="col-span-12 md:col-span-8">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="border border-border p-10 md:p-14 bg-white"
              >
                <CheckCircle2 className="w-8 h-8 text-vermilion mb-6" />
                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
                  Note received.
                </h3>
                <p className="text-ink/70 max-w-lg leading-relaxed">
                  Thank you for writing in. One of our founders will get back to
                  you within two business days — usually sooner.
                </p>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={onSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    label="Full name*"
                    id="full_name"
                    value={form.full_name}
                    onChange={update("full_name")}
                    required
                  />
                  <Field
                    label="Email*"
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                  <Field
                    label="Company"
                    id="company"
                    value={form.company}
                    onChange={update("company")}
                  />
                  <Field
                    label="Role"
                    id="role"
                    value={form.role}
                    onChange={update("role")}
                    placeholder="e.g. Founder / Head of Engineering"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inquiry_type" className="overline text-ink/60">
                    Nature of inquiry
                  </Label>
                  <Select
                    value={form.inquiry_type}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, inquiry_type: v }))
                    }
                  >
                    <SelectTrigger
                      id="inquiry_type"
                      data-testid="field-inquiry_type"
                      className="bg-transparent border-0 border-b border-ink/30 rounded-none h-11 shadow-none focus:ring-0 focus:border-ink"
                    >
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {INQUIRY_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="overline text-ink/60">
                    Your message*
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="field-message"
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    required
                    minLength={10}
                    placeholder="Tell us what you’re trying to build, where you are today, and what a good partnership looks like to you."
                    className="bg-transparent border border-ink/20 focus-visible:ring-ink/30 rounded-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-contact"
                  className="btn-primary disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send message"}{" "}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, ...props }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="overline text-ink/60">
        {label}
      </Label>
      <Input
        id={id}
        data-testid={`field-${id}`}
        {...props}
        className="bg-transparent border-0 border-b border-ink/30 rounded-none h-11 px-0 shadow-none focus-visible:ring-0 focus-visible:border-ink"
      />
    </div>
  );
}
